/** @jsxImportSource @emotion/react */
import React from 'react';
import { Progress, Space, Tag, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import type { BbchInfo, LifecycleProgress, LifecycleTransitionProgress } from '../../../../types/simulation';
import {
  bbchConfidenceLabel,
  bbchCropGroupLabel,
  bbchPrincipalLabel,
  lifecyclePhaseLabel,
  lifecyclePredicateLabel,
} from '../constants';

const { Text } = Typography;

interface LifecycleSummary {
  type?: string | null;
  phase?: string | null;
  yieldMultiplier?: number | null;
  progress?: LifecycleProgress | null;
  bbch?: BbchInfo | null;
}

interface Props {
  lifecycle?: LifecycleSummary | null;
}

const lifecycleTypeLabel = (type?: string | null): string => {
  switch (type) {
    case 'annual':
    case 'herbaceous_perennial':
    case 'woody_perennial':
      return i18n.t(`plantings:lifecycle.type.${type}`);
    default: return type || i18n.t('plantings:lifecycle.typeNotSet');
  }
};

const phaseLabel = (phase?: string | null): string => (
  phase ? (lifecyclePhaseLabel(phase) || phase) : i18n.t('plantings:lifecycle.phaseNotSet')
);

const predicateLabel = (predicate: { id: string; type: string }): string => (
  lifecyclePredicateLabel(predicate.type)
  || lifecyclePredicateLabel(predicate.id)
  || predicate.id
);

const normalizePercent = (value: unknown): number => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, Math.round(num)));
};

const pickBestTransition = (transitions: LifecycleTransitionProgress[]): LifecycleTransitionProgress | null => {
  if (!Array.isArray(transitions) || transitions.length === 0) return null;
  return [...transitions].sort((a, b) => normalizePercent(b.progressPct) - normalizePercent(a.progressPct))[0];
};

export const isDisplayableBbch = (bbch?: BbchInfo | null): boolean => {
  if (!bbch) return false;
  if (bbch.status === 'not_applicable' || bbch.status === 'unmapped') return false;
  return bbch.code != null;
};

export const renderBbchBadge = (bbch?: BbchInfo | null): React.ReactNode => {
  if (!isDisplayableBbch(bbch)) return null;

  const principal = bbch!.principalStage ?? null;
  const principalText = principal != null
    ? i18n.t('plantings:bbch.principalWithCode', { label: bbchPrincipalLabel(principal) || '', code: principal }).trim()
    : '';
  const labelText = bbch!.label ? `— ${bbch!.label}` : '';
  const cropGroupText = bbch!.cropGroup
    ? `\n${i18n.t('plantings:bbch.group', { value: bbchCropGroupLabel(bbch!.cropGroup) || bbch!.cropGroup })}`
    : '';
  const confidenceText = bbch!.confidence
    ? `\n${i18n.t('plantings:bbch.confidence', { value: bbchConfidenceLabel(bbch!.confidence) || bbch!.confidence })}`
    : '';
  const tooltipText = `${principalText} ${labelText}`.trim() + cropGroupText + confidenceText;

  return (
    <Tooltip title={<span css={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>}>
      <Tag color="geekblue">BBCH {bbch!.code}</Tag>
    </Tooltip>
  );
};

const LifecycleProgressCard: React.FC<Props> = ({ lifecycle }) => {
  const { t } = useTranslation('plantings');
  const progress = lifecycle?.progress || null;
  const transitions = progress?.transitions || [];
  const bestTransition = pickBestTransition(transitions);
  const activeFlags = progress?.flags
    ? Object.entries(progress.flags).filter(([, value]) => value)
    : [];
  const modifiers = progress?.modifiers
    ? Object.entries(progress.modifiers).filter(([, value]) => Number.isFinite(Number(value)) && Math.abs(Number(value) - 1) > 0.001)
    : [];

  if (!progress) return null;

  const currentPhase = progress.phase || lifecycle?.phase || null;
  const type = progress.type || lifecycle?.type || null;
  const yieldMultiplier = progress.yieldMultiplier ?? lifecycle?.yieldMultiplier ?? null;
  const bestProgressPct = normalizePercent(bestTransition?.progressPct);

  return (
    <div
      css={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 12,
        background: '#fafafa',
      }}
    >
      <Space direction="vertical" size={10} css={{ width: '100%' }}>
        <Space wrap size={[8, 4]} align="center">
          <Text strong>{t('lifecycle.title')}</Text>
          <Tag color="blue">{lifecycleTypeLabel(type)}</Tag>
          <Tag color={bestTransition?.passed ? 'green' : 'processing'}>{phaseLabel(currentPhase)}</Tag>
          {renderBbchBadge(lifecycle?.bbch)}
          {yieldMultiplier != null && (
            <Text type="secondary">
              {t('lifecycle.yieldLabel')} <Text strong>{Math.round(Number(yieldMultiplier) * 100)}%</Text>
            </Text>
          )}
        </Space>

        {bestTransition ? (
          <Space direction="vertical" size={6} css={{ width: '100%' }}>
            <Space wrap size={[8, 4]} align="center">
              <Text type="secondary">{t('lifecycle.nextPhase')}</Text>
              <Text strong>{phaseLabel(bestTransition.to)}</Text>
              <Tag color={bestTransition.passed ? 'green' : 'orange'}>
                {bestTransition.passed ? t('lifecycle.gatePassed') : `${bestProgressPct}%`}
              </Tag>
            </Space>
            <Progress
              percent={bestProgressPct}
              size="small"
              status={bestTransition.passed ? 'success' : 'active'}
              showInfo={false}
            />
          </Space>
        ) : (
          <Text type="secondary">{t('lifecycle.noActiveTransition')}</Text>
        )}

        {transitions.length > 0 && (
          <Space direction="vertical" size={8} css={{ width: '100%' }}>
            {transitions.map((transition) => (
              <div key={transition.id} css={{ display: 'grid', gap: 6 }}>
                {transitions.length > 1 && (
                  <Space wrap size={[8, 4]}>
                    <Text type="secondary">{phaseLabel(transition.from)} {'->'} {phaseLabel(transition.to)}</Text>
                    <Tag color={transition.passed ? 'green' : 'default'}>
                      {normalizePercent(transition.progressPct)}%
                    </Tag>
                  </Space>
                )}
                <Space wrap size={[6, 4]}>
                  {transition.predicates.map((predicate) => {
                    const pct = normalizePercent(predicate.progressPct);
                    const tag = (
                      <Tag key={predicate.id} color={predicate.satisfied ? 'green' : 'default'}>
                        {predicateLabel(predicate)} · {pct}%
                      </Tag>
                    );
                    return predicate.reason ? (
                      <Tooltip key={predicate.id} title={predicate.reason}>
                        {tag}
                      </Tooltip>
                    ) : tag;
                  })}
                </Space>
              </div>
            ))}
          </Space>
        )}

        {(modifiers.length > 0 || activeFlags.length > 0) && (
          <Space wrap size={[8, 4]}>
            {modifiers.map(([name, value]) => (
              <Tag key={name} color={Number(value) < 1 ? 'volcano' : 'green'}>
                {name}: {Math.round(Number(value) * 100)}%
              </Tag>
            ))}
            {activeFlags.map(([name]) => (
              <Tag key={name} color="purple">{name}</Tag>
            ))}
          </Space>
        )}
      </Space>
    </div>
  );
};

export default LifecycleProgressCard;
